<script setup lang="ts">
import { useUiStore } from '@/stores/ui.store.ts'
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { storeToRefs } from 'pinia'
import OptionsModal from '@/components/OptionsModal.vue'
import { usePreferencesStore } from '@/stores/preferences.ts'
import { RouterView, useRouter } from 'vue-router'
import AdConsentModal from '@/components/AdConsentModal.vue'
import { useInfoStore } from '@/stores/info.ts'
import { useAdmobStore } from '@/stores/admob.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useCatalogSyncStore } from '@/stores/catalog-sync'
import SubscriptionModal from '@/components/SubscriptionModal.vue'
import GeneralDialog from '@/components/GeneralDialog.vue'
import OfflineConsentModal from '@/components/OfflineConsentModal.vue'
import GlobalLoadingBar from '@/components/GlobalLoadingBar.vue'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import { getMainName } from '@/utils'
import { readDeepLinkPath } from '@/utils/startupDeepLink.ts'
import { syncAppBadgeFromUnreadCount } from '@/utils/appBadge.ts'
import { pingUserActivity } from '@/utils/activityPing.ts'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { serverUnavailable } from '@/vendor/apollo-client.ts'
import { EXTERNAL_LINKS } from '@/config/externalLinks.ts'
import { useLocalizationStore } from '@/stores/localization.ts'
import { useSessionStateStore } from '@/stores/sessionState.ts'
import $typo from '@/styles/typografy.module.css'

const { optionsSelectState, optionsSelectOpen, adConsentOpen, subscribeToPlanOpen } =
  toRefs(useUiStore())
const { openSubscribeToPlan } = useUiStore()
const { language, forceOffline } = toRefs(usePreferencesStore())
const { setForceOffline } = usePreferencesStore()
const { isWeb, isDev } = useInfoStore()
const { isOffline } = storeToRefs(useInfoStore())
const { authState, hasPremium, isTester } = toRefs(useAuthStore())
const { canUseOffline, offlineEnabled } = storeToRefs(useCatalogSyncStore())

const openDiscord = () => {
  Browser.open({ url: EXTERNAL_LINKS.discord }).catch(() => {})
}
// Offline is a premium feature: a signed-in non-premium user who goes offline
// (lost network) sees the offline overlay until they reconnect — no login bounce
// on spotty connections. Guarded on userAdmin so a premium user isn't flashed the
// overlay during the auth load window. forceOffline can never strand them because
// it is reset below the moment we know they aren't entitled.
const offlineBlocked = computed(
  () =>
    isOffline.value &&
    authState.value.sessionExists &&
    authState.value.userAdmin !== null &&
    !hasPremium.value &&
    !isTester.value,
)

// forceOffline is a premium/tester affordance (the toggle is gated on
// canUseOffline). If it lingers on for a user who is no longer entitled — e.g. a
// premium plan lapsed while it was set — clear it so it can't pin them offline.
// canUseOffline keeps DEV/native semantics, so dev offline testing is unaffected;
// the userAdmin check avoids clearing a premium user's toggle mid auth-load.
watch(
  [forceOffline, canUseOffline, () => authState.value.userAdmin],
  () => {
    if (forceOffline.value && authState.value.userAdmin !== null && !canUseOffline.value) {
      void setForceOffline(false)
    }
  },
  { immediate: true },
)

watch(
  () => authState.value.sessionExists,
  (sessionExists) => {
    if (sessionExists) {
      void pingUserActivity()
    }
  },
  { immediate: true },
)

const { t } = useLocalizationStore()
const { increaseAdScore } = useAdmobStore()
const router = useRouter()
const sessionState = useSessionStateStore()
const { claimStartupNavigation } = useInfoStore()
const webAccessRestrictedOpen = ref(false)
let pendingWebAccessRestricted = false

onMounted(async () => {
  if (!isWeb) {
    await Keyboard.setResizeMode({ mode: KeyboardResize.None })
    await useAdmobStore().init()
  }
})
router.beforeEach((to) => {
  if (
    isWeb &&
    authState.value.sessionExists &&
    !hasPremium.value &&
    !isTester.value &&
    !to.path.startsWith('/auth') &&
    to.path !== '/settings' &&
    !isDev
  ) {
    pendingWebAccessRestricted = true
    return '/settings'
  }
})
router.afterEach(async (to, from) => {
  if (pendingWebAccessRestricted) {
    pendingWebAccessRestricted = false
    webAccessRestrictedOpen.value = true
  }
  if (getMainName(from.name!.toString()) !== getMainName(to.name!.toString())) {
    await increaseAdScore()
  }
})

if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appUrlOpen', (event) => {
    const deepPath = readDeepLinkPath(event.url)
    if (deepPath) {
      claimStartupNavigation()
      useUiStore().closeDialogsBlockingNavigation()
      Browser.close().catch(() => {})
      router.replace(deepPath).catch((error: unknown) => {
        console.error('Deep link navigation failed', deepPath, error)
      })
    }
  })

  CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    const uiStore = useUiStore()
    if (uiStore.handleBackButton()) {
      return
    }
    if (canGoBack) {
      sessionState.markBackNav()
      router.back()
    } else {
      CapacitorApp.exitApp()
    }
  })

  syncAppBadgeFromUnreadCount()
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      syncAppBadgeFromUnreadCount()
      if (authState.value.sessionExists) {
        void pingUserActivity()
      }
    }
  })
}
</script>

<template>
  <div :key="language" class="h-full">
    <GlobalLoadingBar />
    <RouterView v-slot="{ Component, route }">
      <ErrorBoundary :route-key="route.fullPath">
        <component :is="Component" :key="route.fullPath" />
      </ErrorBoundary>
    </RouterView>
    <OptionsModal
      v-model:visible="optionsSelectOpen"
      :options="optionsSelectState.options"
      :selectedValue="optionsSelectState.selectedValue"
      :headerLabel="optionsSelectState.label"
      @save="optionsSelectState.onSave"
    />
    <AdConsentModal v-if="!isWeb" v-model:visible="adConsentOpen"></AdConsentModal>
    <SubscriptionModal v-model:visible="subscribeToPlanOpen"></SubscriptionModal>
    <OfflineConsentModal />
    <GeneralDialog v-model:visible="webAccessRestrictedOpen">
      <div class="p-7.5 flex flex-col items-center text-center">
        <p :class="$typo.openSans14" class="text-text">
          {{ t('webAccessRestrictedMessage') }}
        </p>
      </div>
    </GeneralDialog>
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="offlineBlocked"
          class="fixed inset-0 z-[900] flex flex-col items-center justify-center gap-5 bg-secondary-background/95 backdrop-blur-sm px-8 text-center"
        >
          <h2 :class="$typo.openSans16" class="font-bold text-text">
            {{ t('offlineGateTitle') }}
          </h2>
          <p :class="$typo.openSans14" class="text-text-muted max-w-xs">
            {{ t('offlineGateMessage') }}
          </p>
          <button
            class="mt-2 rounded-full bg-primary px-6 py-2.5 text-white font-semibold"
            :class="$typo.openSans14"
            @click="openSubscribeToPlan()"
          >
            {{ t('offlineGatePremiumCta') }}
          </button>
        </div>
      </Transition>
    </Teleport>
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="serverUnavailable && !offlineEnabled"
          class="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-4 bg-black/70 backdrop-blur-sm"
        >
          <div
            class="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin"
          />
          <p class="text-white text-sm text-center px-8">{{ t('serverUnavailableMessage') }}</p>
          <button
            type="button"
            class="mt-2 rounded-full bg-white/10 px-5 py-2 text-white text-sm font-semibold active:bg-white/20"
            @click="openDiscord"
          >
            {{ t('serverUnavailableDiscordCta') }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
